import { useEffect, useState, useCallback } from 'react';
import { api, type DigestResponse, type Task } from '../api/client';
import { usePolling } from '../hooks/usePolling';
import { useNavigate } from 'react-router-dom';

const PRIORITY_EMOJI: Record<string, string> = {
  urgent: '🔴', high: '🟠', medium: '🟡', normal: '🟡', low: '🟢'
};

function formatTime(unix: number) {
  return new Date(unix * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function formatRelative(unix: number) {
  const diff = Date.now() / 1000 - unix;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const [digest, setDigest] = useState<DigestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      const hour = new Date().getHours();
      const type = hour < 12 ? 'morning' : hour < 20 ? 'evening' : 'weekly';
      const data = await api.digest(type);
      setDigest(data);
      setLastSync(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePolling(load, { enabled: true });

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return '☀️ Good morning';
    if (h < 18) return '🌤 Good afternoon';
    return '🌙 Good evening';
  })();

  if (loading) return (
    <div className="main-content">
      <p style={{ color: 'var(--text-2)' }}>Loading your digest…</p>
    </div>
  );
  if (!digest) return null;

  const urgentCount = digest.tasks.urgent.length + digest.tasks.overdue.length;

  return (
    <div className="main-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>{greeting}</h1>
          {lastSync && (
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="poll-dot" /> Live sync · last updated {formatRelative(lastSync.getTime() / 1000)}
            </p>
          )}
        </div>
      </div>

      {/* Urgency Banner */}
      {urgentCount > 0 && (
        <div className="urgency-banner" onClick={() => navigate('/tasks')}>
          <span style={{ fontSize: 22 }}>🚨</span>
          <span className="urgency-banner-text">
            {urgentCount} urgent item{urgentCount !== 1 ? 's' : ''} need your attention
          </span>
          <span className="urgency-banner-arrow">›</span>
        </div>
      )}

      {/* Stats Row */}
      <div className="stats-row">
        <a className="stat-card" onClick={() => navigate('/tasks')} style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: 'var(--primary)' }}>{digest.tasks.totalPending}</span>
          <span className="stat-label">Pending Tasks</span>
        </a>
        <a className="stat-card" onClick={() => navigate('/tasks')} style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: 'var(--warning)' }}>{digest.tasks.dueToday.length}</span>
          <span className="stat-label">Due Today</span>
        </a>
        <a className="stat-card" style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: 'var(--success)' }}>{digest.tasks.completedToday}</span>
          <span className="stat-label">Done Today</span>
        </a>
        <a className="stat-card" onClick={() => navigate('/emails')} style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: 'var(--accent)' }}>{digest.emails.untriagedCount}</span>
          <span className="stat-label">Untriaged Emails</span>
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Priority Tasks */}
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/tasks')}>
          <div className="card-header">
            <span className="card-title">✅ Priority Tasks</span>
            {urgentCount > 0 && <span className="badge badge-urgent">🔴 {urgentCount} urgent</span>}
          </div>
          {[...digest.tasks.urgent, ...digest.tasks.dueToday].slice(0, 4).map((task: Task) => (
            <div key={task.id} className="task-row" style={{ borderTop: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span>{PRIORITY_EMOJI[task.priority]}</span>
                  <span className="list-item-title">{task.title}</span>
                </div>
                {task.ai_summary && (
                  <span className="ai-pill">✨ {task.ai_summary}</span>
                )}
              </div>
            </div>
          ))}
          {digest.tasks.totalPending === 0 && (
            <div className="empty-state"><div className="empty-state-emoji">✨</div><p className="empty-state-text">All clear!</p></div>
          )}
          <span className="see-all" onClick={() => navigate('/tasks')}>See all tasks →</span>
        </div>

        {/* Urgent Emails */}
        <div className="card" onClick={() => navigate('/emails')} style={{ cursor: 'pointer' }}>
          <div className="card-header">
            <span className="card-title">📧 Inbox</span>
            <span className="badge badge-urgent">{digest.emails.untriagedCount} untriaged</span>
          </div>
          {digest.emails.urgent.slice(0, 3).map((email) => (
            <div key={email.id} className="list-item">
              <div className="list-item-body">
                <div className="list-item-title">{email.subject}</div>
                <div className="list-item-sub">{email.sender}</div>
                {email.ai_summary && <span className="ai-pill">✨ {email.ai_summary}</span>}
              </div>
            </div>
          ))}
          <span className="see-all">Triage all →</span>
        </div>

        {/* Today's Calendar */}
        <div className="card">
          <div className="card-header"><span className="card-title">📅 Today</span></div>
          {digest.calendar.today.length === 0
            ? <div className="empty-state"><div className="empty-state-emoji">📭</div><p className="empty-state-text">No events today</p></div>
            : digest.calendar.today.map((evt) => {
                const now = Date.now() / 1000;
                const isLive = evt.startAt <= now && evt.endAt >= now;
                return (
                  <div key={evt.id} className="event-row">
                    <div className="event-time">{evt.isAllDay ? 'ALL DAY' : formatTime(evt.startAt)}</div>
                    <div className={`event-accent ${isLive ? 'live' : ''}`} />
                    <div className="event-info">
                      <div className="event-title">{evt.title}</div>
                      {isLive && <span className="live-chip">● LIVE NOW</span>}
                      {evt.location && <div className="event-meta">📍 {evt.location}</div>}
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* LinkedIn */}
        <div className="card" onClick={() => navigate('/linkedin')} style={{ cursor: 'pointer' }}>
          <div className="card-header">
            <span className="card-title">💼 LinkedIn</span>
            {digest.linkedin.pendingCount > 0 && (
              <span className="badge" style={{ background: 'rgba(0,217,255,0.1)', color: 'var(--accent)' }}>
                {digest.linkedin.pendingCount} pending
              </span>
            )}
          </div>
          {digest.linkedin.items.slice(0, 3).map((item) => (
            <div key={item.id} className="list-item">
              <div className="list-item-body">
                <div className="list-item-title">{item.fromName} · {item.type?.replace('_', ' ')}</div>
                {item.fromTitle && <div className="list-item-sub">{item.fromTitle}</div>}
                {item.ai_sentiment && <span className="ai-pill">✨ {item.ai_sentiment}</span>}
              </div>
            </div>
          ))}
          {digest.linkedin.pendingCount === 0 && (
            <div className="empty-state"><div className="empty-state-emoji">🎉</div><p className="empty-state-text">All caught up!</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
